import Joi, { ValidationResult } from 'joi';
import _ from 'lodash';

import { LanguageType } from '@lifeserver/shared';

import {
    BannerActionType,
    BannerPositionPlacement,
    BannerPositionScene,
    BannerType,
} from '../types/enums/bannerType.enum';

import { Banner, CreateOrUpdateBanner } from '../types/bannerTypes';
import { Media } from '../types/mediaTypes';

const languages = Object.values(LanguageType);

export function getLocalizedFieldSchema(
    languages: LanguageType[],
    callback: (lang: LanguageType) => Joi.StringSchema,
) {
    const schema: Partial<Record<LanguageType, Joi.StringSchema>> = {};

    languages.forEach((lang) => {
        schema[lang] = callback(lang);
    });

    return schema;
}

const TitleCharLimitsByType: Record<BannerType, number> = {
    [BannerType.MOON]: 30,
    [BannerType.LANDSCAPE_WITH_BUTTON]: 0,
    [BannerType.LANDSCAPE_SPEECH_BUBBLE]: 60,
    [BannerType.ONBOARDING_SPEECH_BUBBLE]: 60,
    [BannerType.LANDSCAPE_TOP_PANEL]: 60,
    [BannerType.LANDSCAPE_WIDE]: 60,
    [BannerType.PORTRAIT_LARGE]: 60,
    [BannerType.RECTANGULAR_MEDIUM]: 60,
};

const SubtitleCharLimitsByType: Record<BannerType, number> = {
    [BannerType.MOON]: 30,
    [BannerType.LANDSCAPE_WITH_BUTTON]: 0,
    [BannerType.LANDSCAPE_SPEECH_BUBBLE]: 0,
    [BannerType.ONBOARDING_SPEECH_BUBBLE]: 0,
    [BannerType.LANDSCAPE_TOP_PANEL]: 0,
    [BannerType.LANDSCAPE_WIDE]: 0,
    [BannerType.PORTRAIT_LARGE]: 0,
    [BannerType.RECTANGULAR_MEDIUM]: 0,
};

type BannerValidationItem = CreateOrUpdateBanner & {
    activeToDateCopy: Banner['activeToDate'];
    backgroundMediaStatus?: Media['processingStatus'];
};

function getActionParametersSchemaByActionType(
    actionType: BannerValidationItem['actionType'],
) {
    switch (actionType) {
        case BannerActionType.OPEN_TAB:
            return Joi.object({
                tabStringId: Joi.string().required(),
            });
        case BannerActionType.OPEN_PDP:
            return Joi.object({
                packId: Joi.string().required(),
            });
        case BannerActionType.OPEN_DISTRICT:
            return Joi.object({
                districtId: Joi.string().required(),
            });
        case BannerActionType.OPEN_SCENE:
            return Joi.object({
                sceneName: Joi.string().required(),
                xPosition: Joi.number().required(),
            });
        case BannerActionType.OPEN_SHOP:
        default:
            return Joi.object().allow(null);
    }
}

const createSchemas = (banner: BannerValidationItem) => {
    const actionParametersSchema = getActionParametersSchemaByActionType(
        banner.actionType,
    );
    const bannerErrorSchema = Joi.object()
        .options({ abortEarly: false })
        .keys({
            name: Joi.string().required().max(255),
            stringId: Joi.string()
                .max(64)
                .allow(null)
                .optional()
                .pattern(/^[a-zA-Z0-9_]*$/)
                .messages({
                    'string.pattern.base':
                        'String ID can only contain alphanumeric characters, numbers, and underscore',
                }),
            title: Joi.object()
                .keys(
                    getLocalizedFieldSchema(languages, (lang) => {
                        const schema =
                            lang === LanguageType.English
                                ? Joi.string().required()
                                : Joi.string().optional();

                        return schema.allow('').messages({
                            'string.base': `"${lang}" for title must be a type of string`,
                            'string.empty': `"${lang}" for title is missing`,
                            'any.required': `"${lang}" for title is missing`,
                        });
                    }),
                )
                .required(),
            subtitle: Joi.object()
                .keys(
                    getLocalizedFieldSchema(languages, (lang) => {
                        return Joi.string()
                            .optional()
                            .allow('')
                            .messages({
                                'string.base': `"${lang}" for subtitle must be a type of string`,
                                'string.empty': `"${lang}" for subtitle is missing`,
                                'any.required': `"${lang}" for subtitle is missing`,
                            });
                    }),
                )
                .required()
                .allow(null),
            backgroundMediaId: Joi.number().integer().positive().messages({
                'number.base': `Background picture is missing`,
            }),
            backgroundMediaStatus: Joi.any(),
            type: Joi.valid(...Object.values(BannerType)).required(),
            actionType: Joi.valid(
                ...Object.values(BannerActionType),
            ).required(),
            actionParameters: actionParametersSchema,
            positionScene: Joi.valid(
                ...Object.values(BannerPositionScene),
            ).required(),
            positionPlacement: Joi.valid(
                ...Object.values(BannerPositionPlacement),
            ).required(),
            activeToDateCopy: Joi.any(),
            activeFromDate: Joi.date()
                .required()
                .less(Joi.ref('activeToDateCopy'))
                .messages({
                    'date.base': '"Starts on" date must be selected',
                    'date.less':
                        '"Starts on" date must be before "Ends on" date',
                }),
            activeToDate: Joi.date()
                .required()
                .greater(Joi.ref('activeFromDate'))
                .messages({
                    'date.base': '"Ends on" date must be selected',
                    'date.greater':
                        '"Ends on" date must be after "Starts on" date',
                }),
            isDefault: Joi.boolean().optional(),
        });

    const bannerWarningSchema = bannerErrorSchema.keys({
        title: Joi.object()
            .keys(
                getLocalizedFieldSchema(languages, (lang) => {
                    const charLimit = TitleCharLimitsByType[banner.type];

                    return Joi.string()
                        .required()
                        .max(charLimit)
                        .messages({
                            'string.base': `"${lang}" for title must be a type of string`,
                            'string.max': `"${lang}" for title shouldn't exceed ${charLimit} characters`,
                            'string.empty': `"${lang}" for title is missing`,
                            'any.required': `"${lang}" for title is missing`,
                        });
                }),
            )
            .required(),
        subtitle: Joi.object()
            .keys(
                getLocalizedFieldSchema(languages, (lang) => {
                    const charLimit = SubtitleCharLimitsByType[banner.type];

                    return Joi.string()
                        .required()
                        .max(charLimit)
                        .messages({
                            'string.base': `"${lang}" for subtitle must be a type of string`,
                            'string.max': `"${lang}" for subtitle shouldn't exceed ${charLimit} characters`,
                            'string.empty': `"${lang}" for subtitle is missing`,
                            'any.required': `"${lang}" for subtitle is missing`,
                        });
                }),
            )
            .required()
            .allow(null),
        backgroundMediaStatus: Joi.valid('completed', null).messages({
            'any.only':
                'Background picture is still processing or failed to process. Players see a broken image until it is ready.',
        }),
    });

    return {
        bannerWarningSchema,
        bannerErrorSchema,
    };
};

function validate(
    item: CreateOrUpdateBanner,
    {
        backgroundMediaStatus,
    }: { backgroundMediaStatus?: Media['processingStatus'] } = {},
): {
    errors: ValidationResult;
    warnings: ValidationResult;
} {
    const validationItem: BannerValidationItem = Object.assign(
        _.cloneDeep(item),
        {
            activeToDateCopy: item.activeToDate,
            backgroundMediaStatus,
        },
    );
    const { bannerErrorSchema, bannerWarningSchema } =
        createSchemas(validationItem);
    const warnings = bannerWarningSchema.validate(validationItem);
    const errors = bannerErrorSchema.validate(validationItem);
    return { errors, warnings };
}

export default validate;
